import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package } from 'lucide-react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Card, CardContent } from './ui/card';

const AnimatedCategoryCard = ({ category, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  // Get scroll progress for this specific card
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Different animations based on category
  const getFloatingElement = () => {
    switch(category.id) {
      case 'turning':
        return (
          <motion.div
            className="absolute -top-8 -right-8 w-32 h-32 opacity-20"
            animate={{
              rotate: [0, 360],
              y: [0, -15, 0],
            }}
            transition={{
              rotate: { duration: 20, repeat: Infinity, ease: "linear" },
              y: { duration: 3, repeat: Infinity, ease: "easeInOut" }
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <circle cx="50" cy="50" r="30" fill="#2E3A6F" />
              <circle cx="50" cy="50" r="20" fill="white" />
              <path d="M50,30 L50,70 M30,50 L70,50" stroke="#2E3A6F" strokeWidth="3" />
            </svg>
          </motion.div>
        );
      
      case 'milling':
        return (
          <motion.div
            className="absolute -top-8 -right-8 w-24 h-24 opacity-20"
            animate={{
              rotate: [0, -360],
              scale: [1, 1.1, 1],
            }}
            transition={{
              rotate: { duration: 15, repeat: Infinity, ease: "linear" },
              scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <rect x="20" y="20" width="60" height="60" fill="#2E3A6F" transform="rotate(45 50 50)" />
              <rect x="30" y="30" width="40" height="40" fill="white" transform="rotate(45 50 50)" />
            </svg>
          </motion.div>
        );
      
      case 'drilling':
        return (
          <motion.div
            className="absolute -top-12 -right-8 w-16 h-40 opacity-20"
            animate={{
              y: [0, 20, 0],
              rotate: [0, 5, 0, -5, 0],
            }}
            transition={{
              y: { duration: 2, repeat: Infinity, ease: "easeInOut" },
              rotate: { duration: 3, repeat: Infinity, ease: "easeInOut" }
            }}
          >
            <svg viewBox="0 0 40 100" className="w-full h-full">
              <rect x="15" y="0" width="10" height="60" fill="#2E3A6F" />
              <polygon points="20,60 10,80 30,80" fill="#2E3A6F" />
            </svg>
          </motion.div>
        );
      
      case 'grooving':
        return (
          <motion.div
            className="absolute -top-8 -right-8 w-28 h-28 opacity-20"
            animate={{
              rotate: [0, 180, 360],
              scale: [1, 0.9, 1],
            }}
            transition={{
              rotate: { duration: 12, repeat: Infinity, ease: "linear" },
              scale: { duration: 2.5, repeat: Infinity, ease: "easeInOut" }
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <circle cx="50" cy="50" r="35" fill="none" stroke="#2E3A6F" strokeWidth="10" />
              <line x1="50" y1="15" x2="50" y2="85" stroke="#2E3A6F" strokeWidth="5" />
            </svg>
          </motion.div>
        );
      
      case 'parting':
        return (
          <motion.div
            className="absolute -top-8 -right-8 w-32 h-24 opacity-20"
            animate={{
              x: [0, -10, 0],
              rotate: [0, -10, 0],
            }}
            transition={{
              x: { duration: 2, repeat: Infinity, ease: "easeInOut" },
              rotate: { duration: 2, repeat: Infinity, ease: "easeInOut" }
            }}
          >
            <svg viewBox="0 0 100 60" className="w-full h-full">
              <rect x="10" y="20" width="80" height="20" fill="#2E3A6F" />
              <polygon points="90,20 100,30 90,40" fill="#2E3A6F" />
            </svg>
          </motion.div>
        );
      
      case 'vci-packaging':
        return (
          <motion.div
            className="absolute -top-8 -right-8 w-28 h-28 opacity-20"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              scale: { duration: 3, repeat: Infinity, ease: "easeInOut" },
              rotate: { duration: 8, repeat: Infinity, ease: "linear" }
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <rect x="20" y="20" width="60" height="60" fill="none" stroke="#2E3A6F" strokeWidth="4" />
              <path d="M30,30 L70,70 M70,30 L30,70" stroke="#2E3A6F" strokeWidth="2" />
              <circle cx="50" cy="50" r="15" fill="#2E3A6F" opacity="0.5" />
            </svg>
          </motion.div>
        );
      
      default:
        return null;
    }
  };

  // Parallax effect
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 100 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 100 }}
      transition={{ 
        duration: 0.6, 
        delay: index * 0.15,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      <Link to={`/category/${category.id}`}>
        <Card className="group hover:shadow-2xl transition-all duration-500 border-0 bg-white overflow-hidden h-full relative">
          <div className="relative overflow-hidden">
            {/* Floating element specific to category */}
            {getFloatingElement()}
            
            {category.image ? (
              <motion.img 
                src={category.image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${category.image}` : category.image} 
                alt={category.name}
                className="w-full h-56 object-cover"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.5 }}
              />
            ) : (
              <div className="w-full h-56 bg-gradient-to-br from-[#2E3A6F] to-[#1a1f3a] flex items-center justify-center">
                <Package className="text-white/30" size={64} />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
            <motion.div 
              className="absolute bottom-4 left-4"
              whileHover={{ x: 10 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl font-bold text-white">{category.name}</h3>
            </motion.div>
          </div>
          <CardContent className="p-6">
            <p className="text-gray-600 mb-4">{category.description}</p>
            <motion.div 
              className="flex items-center text-[#2E3A6F] font-medium"
              whileHover={{ x: 8 }}
              transition={{ duration: 0.3 }}
            >
              View Products
              <ArrowRight className="ml-2" size={18} />
            </motion.div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
};

export default AnimatedCategoryCard;
